import type { EventMarket } from "hooks/firestore/betting/useBetting";

// list of possible mapN market types
const mapNumberMarketTypes = [
  "MAPN_CORRECT_ROUND_SCORE",
  "MAPN_WINNER_INC_OT",
  "MAPN_ROUNDN_WINNER",
  "MAPN_TOTAL_ROUNDS",
  "MAPN_FIRST_KILL",
  "MAPN_WIN_LOSE_DRAW",
  "MAPN_KILL_HANDICAP",
  "MAPN_KNIFE_ROUND",
  "MAPN_XTH_DRAGON",
  "MAPN_XTH_BARON",
  "MAPN_XTH_INHIBITOR",
  "MAPN_XTH_TOWER",
  "MAPN_XTH_TURRET",
  "MAPN_XTH_AEGIS",
  "MAPN_XTH_BARRACKS",
  "MAPN_WINNER",
  "MAPN_KILL_DRAW_NO_BET",
  "MAPN_PISTOL_ROUND_WINNER",
  "MAPN_FIRST_TO_REACH_X_ROUNDS",
  "MAPN_WINNER",
  "MAPN_NUMBER_OF_ROUNDS_PARITY",
  "MAPN_MEGACREEPS",
  "MAPN_DURATION",
  "MAPN_OVERTIME",
  "MAPN_QUADRA_KILL",
  "NUMBER_OF_MAPS",
  "MAPN_PENTA_KILL",
  "MAPN_ULTRA_KILL",
  "MAPN_BEYOND_GODLIKE",
  "MAPN_RAMPAGE",
  "MAPN_TOTAL_KILLS",
  "MAPN_TOTAL_KILLS_PARITY",
  "MAPN_TOTAL_TURRETS",
  "MAPN_PLACE_WITHIN_TOP",
  "MAPN_TOTAL_TOWERS",
  "MAPN_DRAGON_SLAYED",
  "MAPN_CORRECT_ROUND_SCORE",
  "MAPN_ACTIVATED_RUNE_TYPE_SPAWNED_AT",
  "MAPN_DRAGON_TYPE",
  "MAPN_DRAGON_SOUL_TYPE",
];

const setNMarketTypes = [
  "SETN_WINNER",
  "SETN_TOTAL_GAMES",
  "SETN_GAME_HANDICAP",
  "SETN_CORRECT_SCORE",
];

const filterMapWithSport = (sport: string) => {
  if (filterMapOverrides[sport]) {
    return {
      ...filterMap,
      ...filterMapOverrides[sport], // We add our per sport overrides over the default filterMap
    };
  }

  return filterMap;
};

const NoFullTimeResultMap = {
  "Match Result": ({ marketType }: EventMarket) =>
    [
      "WINNER_INC_OT",
      "WIN_LOSE_DRAW_INC_OT",
      "WINNER_INC_SUPER_OVER",
      "WINNER_INC_OT_PENALTIES",
      "WINNER_INC_EI",
      "DRAW_NO_BET_INC_OT",
      "TO_QUALIFY",
      "WINNER",
      "WIN_LOSE_DRAW",
      "DRAW_NO_BET",
    ].includes(marketType),
  "Full Time Result": () => false,
};

const filterMapOverrides: Record<
  string,
  Record<string, (market: EventMarket) => boolean>
> = {
  BOXING: NoFullTimeResultMap,
  MMA: NoFullTimeResultMap,
  TENNIS: NoFullTimeResultMap,
  CSGO: NoFullTimeResultMap,
  DOTA2: NoFullTimeResultMap,
  LOL: NoFullTimeResultMap,
  RAINBOW_SIX: NoFullTimeResultMap,
  AGE_OF_EMPIRES: NoFullTimeResultMap,
  APEX_LEGENDS: NoFullTimeResultMap,
  ARENA_OF_VALOR: NoFullTimeResultMap,
  BATTLEFIELD_V: NoFullTimeResultMap,
  CALL_OF_DUTY: NoFullTimeResultMap,
  CALL_OF_DUTY_MOBILE: NoFullTimeResultMap,
  E_ICE_HOCKEY: NoFullTimeResultMap,
  EMOTORSPORT: NoFullTimeResultMap,
  FORTNITE: NoFullTimeResultMap,
  HALO_INFINITE: NoFullTimeResultMap,
  HEARTHSTONE: NoFullTimeResultMap,
  KING_OF_GLORY: NoFullTimeResultMap,
  KRUNKER: NoFullTimeResultMap,
  LOL_WILD_RIFT: NoFullTimeResultMap,
  NBA2K: NoFullTimeResultMap,
  OVERWATCH: NoFullTimeResultMap,
  PUBG: NoFullTimeResultMap,
  ROCKET_LEAGUE: NoFullTimeResultMap,
  SMITE: NoFullTimeResultMap,
  STARCRAFT: NoFullTimeResultMap,
  STARCRAFT_1: NoFullTimeResultMap,
  VALORANT: NoFullTimeResultMap,
  WARCRAFT: NoFullTimeResultMap,
  WARCRAFT_3: NoFullTimeResultMap,
};

const filterMap: Record<string, (market: EventMarket) => boolean> = {
  "Match Result": ({ marketType }: EventMarket) =>
    [
      "WINNER_INC_OT",
      "WIN_LOSE_DRAW_INC_OT",
      "WINNER_INC_SUPER_OVER",
      "WINNER_INC_OT_PENALTIES",
      "WINNER_INC_EI",
      "DRAW_NO_BET_INC_OT",
      "TO_QUALIFY",
    ].includes(marketType),

  "Full Time Result": ({ marketType }: EventMarket) =>
    ["WINNER", "WIN_LOSE_DRAW", "DRAW_NO_BET"].includes(marketType),

  Handicap: ({ marketType }: EventMarket) =>
    [
      "HANDICAP",
      "HANDICAP_INC_OT",
      "WINNER_HANDICAP",
      "FIRST_HALF_HANDICAP",
      "SECOND_HALF_HANDICAP",
      "SECOND_HALF_HANDICAP_INC_OT",
      "QUARTERN_HANDICAP",
      "MAP_HANDICAP",
      "MAP_HANDICAP_DRAW",
      "MAPN_ROUND_HANDICAP",
      "MAPN_KILL_HANDICAP",
      "HANDICAP_INC_EI",
      "SETN_GAME_HANDICAP",
      "GAME_HANDICAP",
      "SET_HANDICAP",
    ].includes(marketType),

  "Map Markets": ({ marketType }: EventMarket) =>
    ["NUMBER_OF_MAPS"].includes(marketType),

  "Round Markets": ({ marketType }: EventMarket) =>
    ["MAPN_ROUND_HANDICAP", "MAPN_TOTAL_ROUNDS"].includes(marketType),

  "1st Half Markets": ({ marketType }: EventMarket) =>
    marketType === "FIRST_HALF_WINNER",
  "2nd Half Markets": ({ marketType }: EventMarket) =>
    marketType === "SECOND_HALF_WINNER",

  "1st Map Markets": ({ marketType, specifiers }: EventMarket) =>
    mapNumberMarketTypes.includes(marketType) && specifiers?.map === "1",
  "2nd Map Markets": ({ marketType, specifiers }: EventMarket) =>
    mapNumberMarketTypes.includes(marketType) && specifiers?.map === "2",
  "3rd Map Markets": ({ marketType, specifiers }: EventMarket) =>
    mapNumberMarketTypes.includes(marketType) && specifiers?.map === "3",
  "4th Map Markets": ({ marketType, specifiers }: EventMarket) =>
    mapNumberMarketTypes.includes(marketType) && specifiers?.map === "4",
  "5th Map Markets": ({ marketType, specifiers }: EventMarket) =>
    mapNumberMarketTypes.includes(marketType) && specifiers?.map === "5",

  "1st Period Markets": ({ marketType, specifiers }: EventMarket) =>
    marketType === "PERIODN_DRAW_NO_BET" && specifiers?.periodnr === "1",
  "2nd Period Markets": ({ marketType, specifiers }: EventMarket) =>
    marketType === "PERIODN_DRAW_NO_BET" && specifiers?.periodnr === "2",
  "3rd Period Markets": ({ marketType, specifiers }: EventMarket) =>
    marketType === "PERIODN_DRAW_NO_BET" && specifiers?.periodnr === "3",

  "1st Set Markets": ({ marketType, specifiers }: EventMarket) =>
    setNMarketTypes.includes(marketType) && specifiers?.setnr === "1",
  "2nd Set Markets": ({ marketType, specifiers }: EventMarket) =>
    setNMarketTypes.includes(marketType) && specifiers?.setnr === "2",
  "3rd Set Markets": ({ marketType, specifiers }: EventMarket) =>
    setNMarketTypes.includes(marketType) && specifiers?.setnr === "3",
  "4th Set Markets": ({ marketType, specifiers }: EventMarket) =>
    setNMarketTypes.includes(marketType) && specifiers?.setnr === "4",
  "5th Set Markets": ({ marketType, specifiers }: EventMarket) =>
    setNMarketTypes.includes(marketType) && specifiers?.setnr === "5",

  "Game Markets": ({ marketType }: EventMarket) =>
    ["TOTAL_GAMES", "HOME_TOTAL_GAMES", "AWAY_TOTAL_GAMES"].includes(
      marketType,
    ),

  "Set Markets": ({ marketType }: EventMarket) =>
    [
      "HOME_TO_WIN_A_SET",
      "AWAY_TO_WIN_A_SET",
      "ANY_SET_TO_NIL",
      "EXACT_SETS",
    ].includes(marketType),

  Total: ({ marketType }: EventMarket) =>
    [
      "TOTAL",
      "TOTAL_INC_OT",
      "FIRST_HALF_TOTAL",
      "SECOND_HALF_TOTAL",
      "TOTAL_MAPS",
      "HOME_TOTAL_INC_OT",
      "AWAY_TOTAL_INC_OT",
      "FIRST_HALF_TOTAL",
      "SECOND_HALF_TOTAL_INC_OT",
      "TOTAL_INC_OT_PENALTIES",
      "TOTAL_INC_EI",
      "HOME_TOTAL_INC_EI",
      "AWAY_TOTAL_INC_EI",
      "SETN_TOTAL_GAMES",
    ].includes(marketType),

  "Odd/Even Markets": ({ marketType }: EventMarket) =>
    [
      "ODD_EVEN_INC_OT",
      "HOME_ODD_EVEN",
      "AWAY_ODD_EVEN",
      "HOME_ODD_EVEN_INC_OT",
      "ODD_EVEN",
      "AWAY_ODD_EVEN_INC_OT",
      "FIRST_OVER_ODD_EVEN",
      "INNINGSN_ODD_EVEN",
      "ODD_EVEN_GAMES",
      "FIRST_HALF_ODD_EVEN",
      "SECOND_HALF_ODD_EVEN",
      "QUARTERN_ODD_EVEN",
      "ODD_EVEN_INC_EI",
    ].includes(marketType),

  "First Half Markets": ({ marketType }: EventMarket) =>
    [
      "FIRST_HALF_WIN_LOSE_DRAW",
      "FIRST_HALF_DRAW_NO_BET",
      "FIRST_HALF_HOME_TOTAL",
      "FIRST_HALF_AWAY_TOTAL",
      "FIRST_HALF_HANDICAP",
      "FIRST_HALF_ODD_EVEN",
      "FIRST_HALF_TOTAL",
    ].includes(marketType),

  "Second Half Markets": ({ marketType }: EventMarket) =>
    [
      "SECOND_HALF_WIN_LOSE_DRAW",
      "SECOND_HALF_WIN_LOSE_DRAW_INC_OT",
      "SECOND_HALF_DRAW_NO_BET",
      "SECOND_HALF_HOME_TOTAL",
      "SECOND_HALF_AWAY_TOTAL",
      "SECOND_HALF_HANDICAP",
      "SECOND_HALF_HANDICAP_INC_OT",
      "SECOND_HALF_ODD_EVEN",
      "SECOND_HALF_TOTAL_INC_OT",
      "SECOND_HALF_TOTAL",
    ].includes(marketType),

  "1st Quarter Markets": ({ marketType, specifiers }: EventMarket) =>
    [
      "QUARTERN_ODD_EVEN",
      "QUARTERN_WIN_LOSE_DRAW",
      "QUARTERN_DRAW_NO_BET",
      "QUARTERN_TOTAL",
      "QUARTERN_HANDICAP",
    ].includes(marketType) && specifiers?.quarternr === "1",

  "2nd Quarter Markets": ({ marketType, specifiers }: EventMarket) =>
    [
      "QUARTERN_ODD_EVEN",
      "QUARTERN_WIN_LOSE_DRAW",
      "QUARTERN_DRAW_NO_BET",
      "QUARTERN_TOTAL",
      "QUARTERN_HANDICAP",
    ].includes(marketType) && specifiers?.quarternr === "2",

  "3rd Quarter Markets": ({ marketType, specifiers }: EventMarket) =>
    [
      "QUARTERN_ODD_EVEN",
      "QUARTERN_WIN_LOSE_DRAW",
      "QUARTERN_DRAW_NO_BET",
      "QUARTERN_TOTAL",
      "QUARTERN_HANDICAP",
    ].includes(marketType) && specifiers?.quarternr === "3",

  "4th Quarter Markets": ({ marketType, specifiers }: EventMarket) =>
    [
      "QUARTERN_ODD_EVEN",
      "QUARTERN_WIN_LOSE_DRAW",
      "QUARTERN_DRAW_NO_BET",
      "QUARTERN_TOTAL",
      "QUARTERN_HANDICAP",
    ].includes(marketType) && specifiers?.quarternr === "4",

  // Player markets
  "Player Points": ({ marketType }: EventMarket) =>
    [
      "PLAYER_3POINTS_INC_OT",
      "PLAYER_FIELD_GOALS_INC_OT",
      "PLAYER_POINTS_INC_OT",
      "PLAYER_TOTAL_POINTS_INC_OT",
    ].includes(marketType),
  "Player Assists": ({ marketType }: EventMarket) =>
    [
      "PLAYER_TOTAL_ASSISTS_INC_OT",
      "PLAYER_ASSISTS",
      "PLAYER_ASSISTS_INC_OT",
    ].includes(marketType),
  "Player Blocks": ({ marketType }: EventMarket) =>
    ["PLAYER_BLOCKS_INC_OT"].includes(marketType),
  "Player Rebounds": ({ marketType }: EventMarket) =>
    ["PLAYER_REBOUNDS_INC_OT", "PLAYER_TOTAL_REBOUNDS_INC_OT"].includes(
      marketType,
    ),
  "Player Steals": ({ marketType }: EventMarket) =>
    ["PLAYER_STEALS_INC_OT"].includes(marketType),
  "Player 3's": ({ marketType }: EventMarket) =>
    marketType === "PLAYER_TOTAL_3POINTS_INC_OT",

  // afl
  "Player Carries": ({ marketType }: EventMarket) =>
    marketType === "PLAYER_CARRIES",
  "Player Pass Completions": ({ marketType }: EventMarket) =>
    marketType === "PLAYER_PASS_COMPLETIONS",
  "Player Passing Touchdowns": ({ marketType }: EventMarket) =>
    marketType === "PLAYER_PASSING_TOUCHDOWNS",
  "Player Passing Yards": ({ marketType }: EventMarket) =>
    marketType === "PLAYER_PASSING_YARDS",
  "Player Receiving Yards": ({ marketType }: EventMarket) =>
    marketType === "PLAYER_RECEIVING_YARDS",
  "Player Receptions": ({ marketType }: EventMarket) =>
    marketType === "PLAYER_RECEPTIONS",
  "Player Rushing Yards": ({ marketType }: EventMarket) =>
    marketType === "PLAYER_RUSHING_YARDS",

  // soccer - player
  "Player Goals": ({ marketType }: EventMarket) =>
    marketType === "PLAYER_GOALS",
  "Player Goalscorer": ({ marketType }: EventMarket) =>
    [
      "FIRST_GOALSCORER",
      "ANYTIME_GOALSCORER",
      "LAST_GOALSCORER",
      "ANYTIME_GOALSCORER_AND_CORRECT_SCORE",
      "FIRST_GOALSCORER_AND_CORRECT_SCORE",
      "ANYTIME_GOALSCORER_AND_1X2",
      "FIRST_GOALSCORER_AND_1X2",
    ].includes(marketType),
  "Player Shots": ({ marketType }: EventMarket) =>
    ["PLAYER_SHOTS", "PLAYER_SHOTS_ON_GOAL"].includes(marketType),
  "Player Passes": ({ marketType }: EventMarket) =>
    marketType === "PLAYER_PASSES",
  "Player Tackles": ({ marketType }: EventMarket) =>
    marketType === "PLAYER_TACKLES",

  // Baseball - player
  "Batter Markets": ({ marketType }: EventMarket) =>
    [
      "BATTER_HITS",
      "BATTER_HOME_RUNS",
      "BATTER_RUNS_BATTED_IN",
      "BATTER_RUNS",
      "BATTER_TOTAL_BASES",
      "BATTER_RUNS_RUNS_BATTED_IN",
    ].includes(marketType),
  "Pitcher Markets": ({ marketType }: EventMarket) =>
    ["PITCHER_STRIKEOUTS", "PITCHER_EARNED_RUNS"].includes(marketType),

  // cricket
  "Innings Markets": ({ marketType }: EventMarket) =>
    [
      "INNINGSN_HOME_RUN_RANGE",
      "INNINGSN_AWAY_RUN_RANGE",
      "INNINGSN_HOME_TOTAL",
      "INNINGSN_AWAY_TOTAL",
      "INNINGSN_OVERSN_HOME_RUN_RANGE",
      "INNINGSN_OVERSN_AWAY_RUN_RANGE",
      "INNINGSN_OVERSN_HOME_TOTAL",
      "INNINGSN_OVERSN_AWAY_TOTAL",
      "INNINGSN_HIGHEST_SCORING_OVER",
      "INNINGSN_WIN_LOSE_DRAW",
      "INNINGS_WINNER",
      "INNINGSN_TOTAL",
    ].includes(marketType),
};

const groupMarketsFallback = (markets: EventMarket[], sport: string) => {
  const ADDITIONAL_MARKETS_LABEL = "Additional Markets";

  const keys = Object.keys(filterMapWithSport(sport));

  //Grouped markets are stored in an object.
  //The ordering relies on the fact that Object.keys will iterate over keys in insertion order.
  let groupedMarkets = {
    // get the group from the market.catergory
    ...markets.reduce<Record<string, EventMarket[]>>(
      (groupedMarkets, market) => {
        const groupName = market.category;
        if (groupName) {
          if (!groupedMarkets[groupName]) {
            groupedMarkets[groupName] = [];
          }
          groupedMarkets[groupName].push(market);
        }
        return groupedMarkets;
      },
      {},
    ),
    // get the Group from presets
    ...keys.reduce<Record<string, EventMarket[]>>(
      (groupedMarkets, groupName) => {
        const marketsFound = markets.filter(
          filterMapWithSport(sport)[groupName],
        );

        if (marketsFound.length) {
          groupedMarkets[groupName] = marketsFound;
        }

        return groupedMarkets;
      },
      {},
    ),
  };

  groupedMarkets = sortGroupNames(groupedMarkets, sport);

  // Push uncategorised markets to "other" group
  const groupedMarketIds = Object.values(groupedMarkets)
    .flat()
    .map((market: EventMarket) => market.id);

  markets
    .filter((market) => !groupedMarketIds.includes(market.id))
    .forEach((market) => {
      if (!groupedMarkets[ADDITIONAL_MARKETS_LABEL]) {
        groupedMarkets[ADDITIONAL_MARKETS_LABEL] = [];
      }
      groupedMarkets[ADDITIONAL_MARKETS_LABEL].push(market);
    });

  return groupedMarkets;
};

export const MARKET_GROUPS = ["Match Result"];
/**
 * To customise the order of the groups for each sport.
 *
 * Show the Full time result group for soccer and rugby but the h2h for all others sports.
 * Typically, after the first group we fall back to the default ordering defined by the order of filterMap
 */
export const SPORT_MARKET_GROUP_ORDERING: Record<string, string[]> = {
  FIFA: ["Full Time Result"].concat(MARKET_GROUPS),
  SOCCER: ["Full Time Result"].concat(MARKET_GROUPS),
  RUGBY: ["Full Time Result"].concat(MARKET_GROUPS),
};

const sortGroupNames = (
  groupedMarkets: Record<string, EventMarket[]>,
  sport: string,
) => {
  const marketGroups = SPORT_MARKET_GROUP_ORDERING[sport] || MARKET_GROUPS;

  // Order properties in the object by the order of MARKET_GROUPS
  const orderedGroupedMarkets: Record<string, EventMarket[]> = {};
  marketGroups.forEach((groupName) => {
    if (groupedMarkets[groupName]) {
      orderedGroupedMarkets[groupName] = groupedMarkets[groupName];
      delete groupedMarkets[groupName];
    }
  });

  return { ...orderedGroupedMarkets, ...groupedMarkets };
};

export default groupMarketsFallback;
